import * as vp from 'vopay';
import { FunctionComponent, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { FormikErrors, FormikTouched } from 'formik';
import moment from 'moment';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { SingleCollection, NewAccountData, FinancialInstitutionsType } from './types';
import SingleCollectionForm from './SingleCollectionForm';

import { PendingDataProvider } from '@/providers/PendingData.provider';
import useFetchClientCreditCards from '@/hooks/useApi/useFetchClientCreditCards';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useClientData } from '@/providers/ClientData.provider';
import InteracInfoContext, { InteracDetails } from '@/components/Accounts/AccountCreation/Interac/InteracInfoContext';
import SingleCollectionBankInfoContext from './SingleCollectionBankInfoContext';
import usePostIQ11Tokenize from '@/hooks/useApi/usePostIQ11Tokenize';
import useFetchFinancialInstitutions from '@/hooks/useApi/useFetchFinancialInstitutions';
import useFetchFinancialInstitutionBranches from '@/hooks/useApi/useFetchFinancialInstitutionBranches';
import usePostEftFund from '@/hooks/useApi/usePostEftFund';
import usePostScheduledEftFund from '@/hooks/useApi/usePostScheduledEftFund';
import usePostInteracMoneyRequest from '@/hooks/useApi/usePostInteracMoneyRequest';
import { SingleCollectionCtx } from '../Dialogue/SingleCollectionContext';
import { SingleCollectionDialogueHandler, DialogueHandlerProps } from '../Dialogue/types';
import usePostSetMyClientBankAccount from '@/hooks/useApi/usePostSetMyClientBankAccount';
import usePostACHFund from '@/hooks/useApi/usePostACHFund';
import usePostACHFundSchedule from '@/hooks/useApi/usePostACHFundSchedule';
import usePostChargeCreditCard from '@/hooks/useApi/usePostChargeCreditCard';
import ElinxSettingsDialogue from '@/components/Elinx/ElinxSettings/ElinxSettingsDialogue';
import PaylinkDialogue from '@/components/Accounts/AccountCreation/Dialogue/Paylink/PaylinkDialogue';
import {
  SendPaylinkDialogueHandler,
  DialogueHandlerProps as DialogueHandlerProps2,
} from '@/components/Accounts/AccountCreation/Dialogue/types';
import SendPaylinkContext, {
  SendPaylinkCtx,
} from '@/components/Accounts/AccountCreation/Dialogue/Paylink/PaylinkContext';
import PaylinkCreationPage from '@/components/Accounts/AccountCreation/PaylinkCreationPage';
import useFetchElinxSettings from '@/hooks/useApi/useFetchElinxSettings';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import useFetchInteracEmail from '@/hooks/useApi/useFetchInteracEmail';

type TokenProps = {
  token: string;
  cardToken: string;
  type: string;
  currency?: string | null;
  interacID: string;
  accountHolderName?: string;
  account?: {
    RecipientPhoneNumber?: string;
    RecipientName?: string;
    RecipientEmailAddress?: string;
    Question?: string;
    Answer?: string;
    ClientReferenceNumber?: string;
  };
};

type EventMessageData = {
  Step: 'LINK';
  Institution: string;
  Url: string;
  Token: string;
  BankId: string;
  PaymentMethod: 'bank' | 'creditcard' | 'debitcard';
  [key: string]: string;
};
type EventMessage = {
  data: EventMessageData;
};

const { Box, Title, toBool } = vp;

const SingleCollectionContainer: FunctionComponent = () => {
  const { userSettings } = useUserSettingsProvider();
  const reviewRef = useRef<HTMLDivElement>(null);
  const { state } = useLocation<TokenProps>();
  const dialog = useAlertDialog();
  const history = useHistory();
  const { setOpenModule } = useOpenModule();
  const typeProp = state?.type;
  const tokenProp = state?.token || '';
  const currencyProp = state?.currency;
  const accountHolderName = state?.accountHolderName;

  const { creditCards, fetchCreditCards, success } = useFetchClientCreditCards();

  const contactCreditCardMap = useMemo(
    () =>
      creditCards.reduce<{
        [token: string]: vp.ClientCreditCardLists;
      }>((acc, card) => {
        return {
          ...acc,
          [card.CreditCardToken]: card,
        };
      }, {}),
    [creditCards],
  );

  const {
    response: { ClientAccountsEnabled, Country, AccountID, AccountName, LoginUserName },
  } = useClientData();
  const [clientBankAccountsResponse, fetchClientBankAccounts, resetBankAccount] = useFetchClientBankAccounts({});

  const [financialInstitutionsResp, fetchFinancialInstitutions] = useFetchFinancialInstitutions({});
  const [financialInstitutionBranchesResponse, fetchFinancialInstitutionBranches] =
    useFetchFinancialInstitutionBranches({});
  const [FinancialInstitutions, setFormattedFinancialInstitutions] = useState<FinancialInstitutionsType>({});
  const [FormattedFinancialInstitutionBranches, setFormattedFinancialInstitutionBranches] =
    useState<FinancialInstitutionsType>({});
  const [eftFundResponse, postEftFund] = usePostEftFund({});
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [showSingleCollectionErrorAlert, setShowSingleCollectionErrorAlert] = useState(false);
  const [showSingleCollectionConfirmAlert, setShowSingleCollectionConfirmAlert] = useState(false);
  const [singleCollectionError, setSingleCollectionError] = useState('');
  const [shouldFundEft, setShouldFundEft] = useState(false);
  const [isCreateAccount, setCreateAccount] = useState(false);
  const [scheduledEftFundResponse, postScheduledEftFund] = usePostScheduledEftFund({});
  const [shouldFundScheduledEft, setShouldFundScheduledEft] = useState(false);
  const [shouldTokenize, setShouldTokenize] = useState(false);
  const [interacMoneyRequestResponse, postInteracMoneyRequest] = usePostInteracMoneyRequest({});
  const [shouldFundInteracETransfer, setShouldFundInteracETransfer] = useState(false);
  const [singleCollectionValuesPresent, setSingleCollectionValuesPresent] = useState(false);
  const [submissionConfirmed, setSubmissionConfirmed] = useState(false);
  const [interacEmailsResponse, fetchInteracEmails] = useFetchInteracEmail({});
  const [singleCollectionValues, setSingleCollectionValues] = useState<SingleCollection>({
    ABARoutingNumber: '',
    AccountConnectionMethod: '',
    AccountNumber: '',
    Address1: '',
    Address2: '',
    Amount: 0,
    Answer: '',
    BranchTransitNumber: '',
    City: '',
    ClientAccount: '',
    ClientReferenceNumber: '',
    CompanyName: '',
    Contact: '',
    ContactType: '',
    Country: '',
    Currency: '',
    Description: '',
    EndDate: '',
    Ending: '',
    EndingAfterPayments: '',
    EtransferConnectionType: '',
    FinancialInstitutionNumber: '',
    FirstName: '',
    Frequency: '',
    IdentificationNumber: '',
    InvoiceNumber: '',
    LastName: '',
    Message: '',
    MessageForRecipient: '',
    Nickname: '',
    Notes: '',
    PaymentMethod: '',
    PostalCode: '',
    ProvideOrRequestPaymentInformation: '',
    Province: '',
    Question: '',
    RecipientEmailAddress: '',
    RecipientName: '',
    RecipientPhoneNumber: '',
    ScheduleType: '',
    SemiMonthlyDates: '',
    SendEmail: false,
    SenderName: '',
    StartDate: '',
    TakeFromAccountOn: '',
    TransactionLabel: '',
    SendNotification: false,
    SendReminder: false,
    ReminderFrequency: '',
    NotificationMethod: 'email',
    SecCode: 'PPD',
  });
  const [setDefautlBankAccountResponse, setDefautlBankAccount] = usePostSetMyClientBankAccount({});
  const [ACHFundResponse, postACHFund] = usePostACHFund({});
  const [shouldACHFund, setShouldACHFund] = useState(false);
  const [shouldACHFundSchedule, setShouldACHFundSchedule] = useState(false);
  const [ACHFundScheduleResponse, postACHFundSchedule] = usePostACHFundSchedule({});
  const [chargeCreditCardResponse, postChargeCreditCard] = usePostChargeCreditCard({});

  const [embedUrlResponse, postEmbedUrlResponse] = usePostEmbedUrl({});
  const [paylinkResponse, postSendPaylink] = usePostSendPaylink({});
  const [, , , fetchElinxSettings] = useFetchElinxSettings({});
  const [selectedCurrency, setSelectedCurrency] = useState(
    currencyProp ? currencyProp : Country === 'US' ? 'USD' : 'CAD',
  );
  const SingleCollectionBankInfoCtx = useContext(SingleCollectionBankInfoContext);
  const selectedPaymentMethod = typeProp !== undefined ? typeProp : 'Bank Account';

  useEffect(() => {
    if (clientBankAccountsResponse.Success && tokenProp !== '') {
      setBankAccountInfo(clientBankAccountsResponse.BankAccounts[0]);
      setSelectedBank(clientBankAccountsResponse.BankAccounts[0]);
      SingleCollectionBankInfoCtx.updateBankAccount({
        ...clientBankAccountsResponse.BankAccounts[0],
      });
    }
  }, [clientBankAccountsResponse]);

  const [fundRequest, setFundRequest] = useState<SingleCollection>({
    ABARoutingNumber: '',
    AccountConnectionMethod: 'Manual',
    AccountNumber: '',
    Address1: '',
    Address2: '',
    Amount: 0,
    Answer: '',
    BranchTransitNumber: '',
    ClientAccount: '',
    ClientReferenceNumber: '',
    City: '',
    Currency: selectedCurrency,
    CompanyName: '',
    Contact: tokenProp,
    ContactName: '',
    ContactType: 'Individual',
    Country,
    Description: '',
    EndDate: '',
    Ending: 'End Date',
    EndingAfterPayments: '',
    EtransferConnectionType: 'email',
    FinancialInstitutionNumber: '',
    FirstName: '',
    Frequency: 'monthly',
    LastName: '',
    Message: '',
    Nickname: '',
    Notes: '',
    InvoiceNumber: '',
    IdentificationNumber: '',
    MessageForRecipient: '',
    PaymentMethod:
      selectedPaymentMethod === 'Interac e-Transfer' ? 'Interac e-Transfer Money Request' : selectedPaymentMethod,
    BankingPaymentRail: Country === 'CA' ? 'eft' : 'ach',
    PostalCode: '',
    ProvideOrRequestPaymentInformation: 'provide',
    Province: '',
    Question: '',
    RecipientName: '',
    RecipientPhoneNumber: '',
    RecipientEmailAddress: '',
    ScheduleType: 'single',
    SemiMonthlyDates: '',
    SendEmail: false,
    SenderName: AccountName,
    StartDate: String(Date.now()),
    TakeFromAccountOn: String(Date.now()),
    TransactionLabel: '',
    SendNotification: false,
    SendReminder: false,
    ReminderFrequency: '7',
    NotificationMethod: 'email',
    ReceiverPhoneNumber: '',
  });

  const [view, viewState] = useState<'form' | 'paylink'>('form');
  const [formErrors, setFormErrors] = useState<FormikErrors<SingleCollection>>({});
  const [formTouched, setFormTouched] = useState<FormikTouched<SingleCollection>>({});
  const { getModuleTitle, setSubNavActiveModule, onSubNavClick } = useNavMenu();
  const [submitTransaction, setSubmitTransaction] = useState(false);

  const [addedCreditCard, setAddedCreditCard] = useState<vp.ClientCreditCardLists>();
  const [isAddingNewCreditCard, setIsAddingNewCreditCard] = useState(false);
  const [contactClientAccountID, setContactClientAccountID] = useState<string | undefined>(undefined);
  const [showElinxConnect, setShowElinxConnect] = useState(false);
  const [shouldChargeCreditCard, setShouldChargeCreditCard] = useState(false);

  const [selectedCreditCard, setSelectedCreditCard] = useState<vp.ClientCreditCardLists>({
    ClientAccountID: '',
    CreditCardBrand: '',
    CreditCardExpiryMonth: '',
    CreditCardExpiryYear: '',
    CreditCardHolderName: '',
    CreditCardNumber: '',
    CreditCardToken: '',
    IsDefault: '',
  });

  const [isVisible, setIsVisible] = useState(false);
  const [shouldPostEmbedUrl, setShouldPostEmbedUrl] = useState(false);
  const [shouldSendPaylink, setShouldSendPaylink] = useState(false);
  const [showSendPaylinkSuccessAlert, setShowSendPaylinkSuccessAlert] = useState(false);
  const [showSendPaylinkErrorAlert, setShowSendPaylinkErrorAlert] = useState(false);
  const [sendPaylinkError, setSendPaylinkError] = useState('');
  const [payLink, setPayLink] = useState('');
  const [clientAccountDetail, setSelectedClientAccountDetail] = useState<vp.ClientAccount>();
  const [hasNoBankAccount, setHasNoBankAccount] = useState(false);
  const [hasNoCreditCard, setHasNoCreditCard] = useState(false);
  const [hasNoInterac, setHasNoInterac] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>(undefined);

  const [bankAccountInfo, setBankAccountInfo] = useState<vp.ClientBankAccountsLists | undefined>({
    ABARoutingNumber: '',
    AccountHolderName: '',
    AccountNumber: '',
    Address: '',
    BankID: '',
    BranchTransitNumber: '',
    City: '',
    ClientAccountID: '',
    ClientControlledBankAccount: '',
    ClientReferenceNumber: '',
    Currency: '',
    DateAdded: '',
    EmailAddress: '',
    FinancialInstitutionNumber: '',
    FlinksAccountID: '',
    FlinksLoginID: '',
    InstitutionName: '',
    InveriteRequestGUID: '',
    IsDefault: '',
    Phone: '',
    PlaidAccountID: '',
    'PostalCode/ZipCode': '',
    'Province/State': '',
    Token: '',
    FiservToken: '',
    IntegrationType: '',
    MxAuthorizationCode: '',
    MxProcessorToken: '',
    PlaidProcessorToken: '',
  });

  const [interacInfo, setInteracInfo] = useState({
    isInteracQuestionsRequired: false,
    addInteracEmail: false,
    interacDetails: {
      RecipientPhoneNumber: '',
      RecipientName: '',
      RecipientEmailAddress: '',
      Question: '',
      Answer: '',
      ClientReferenceNumber: '',
    },
  });
  const [selectedBank, setSelectedBank] = useState<vp.ClientBankAccountsLists>();
  const [tokenizeResponse, postTokenize] = usePostIQ11Tokenize({});
  const [resetPendingData, shouldResetPendingData] = useState(false);
  const [canSubmitTransaction, setCanSubmitTransaction] = useState(false);

  const SendPaylink: SendPaylinkCtx = {
    showSendPaylinkSuccessAlert,
    showSendPaylinkErrorAlert,
    sendPaylinkError,
    payLink,
  };

  const updateBankAccount = (bankDetails?: vp.ClientBankAccountsLists) => {
    setBankAccountInfo(bankDetails);
  };

  const updateInterac = (
    addInteracEmail: boolean,
    isInteracQuestionsRequired?: boolean,

    interacDetails?: InteracDetails,
  ) => {
    setInteracInfo({
      addInteracEmail,
      isInteracQuestionsRequired: isInteracQuestionsRequired || false,

      interacDetails: {
        ...interacInfo.interacDetails,
        ...interacDetails,
        ClientReferenceNumber: interacDetails?.ClientReferenceNumber || '',
      },
    });
  };

  const [interacInfoWithUpdate, setInteracInfoWithUpdate] = useState({
    isInteracQuestionsRequired: false,
    addInteracEmail: false,
    interacDetails: {
      RecipientPhoneNumber: '',
      RecipientName: '',
      RecipientEmailAddress: '',
      Question: '',
      Answer: '',
      ClientReferenceNumber: '',
    },
    updateInterac,
  });

  const [common, setCommon] = useState({
    PortalUser: '',
    Amount: 0,
    FinancialInstitutionNumber: '',
    BranchTransitNumber: '',
    AccountNumber: '',
    ABARoutingNumber: '',
    FirstName: '',
    LastName: '',
    CompanyName: '',
    PhoneNumber: '',
    Address1: '',
    City: '',
    Province: '',
    Country: '',
    Token: '',
    PostalCode: '',
    ClientReferenceNumber: '',
    FlinksAccountID: '',
    FlinksLoginID: '',
    TransactionLabel: '',
  });

  useEffect(() => {
    fetchCreditCards({
      PortalUser: LoginUserName,
      ClientControlled: ClientAccountsEnabled === '1',
    });
  }, []);

  useEffect(() => {
    setInteracInfoWithUpdate({
      ...interacInfo,
      updateInterac,
    });
  }, [interacInfo]);

  useEffect(() => {
    if (state?.interacID !== undefined && state?.account !== undefined) {
      setInteracInfo({
        isInteracQuestionsRequired: true,
        addInteracEmail: false,
        interacDetails: {
          RecipientPhoneNumber: state?.account.RecipientPhoneNumber || '',
          RecipientName: state?.account.RecipientName || '',
          RecipientEmailAddress: state?.account.RecipientEmailAddress || '',
          Question: state?.account.Question || '',
          Answer: state?.account.Answer || '',
          ClientReferenceNumber: state?.account.ClientReferenceNumber || '',
        },
      });
    }
  }, [state?.interacID]);

  useEffect(() => {
    if (clientAccountDetail) {
      if (fundRequest.PaymentMethod === 'Bank Account') {
        fetchClientBankAccounts({
          ClientAccountID: contactClientAccountID,
          Timezone: userSettings.timezone,
        });
      } else if (getAccountCreationValue(fundRequest.PaymentMethod) === 'e-transfer') {
        fetchInteracEmails({
          ClientAccountID: contactClientAccountID,
          RecipientName: '',
          RecipientEmailAddress: '',
          ClientReferenceNumber: '',
          ClientControlled: false,
        });
      } else if (fundRequest.PaymentMethod === 'Credit Card') {
        fetchCreditCards({
          PortalUser: LoginUserName,
          ClientControlled: false,
          ClientAccountID: contactClientAccountID,
        });
      }
    }
  }, [contactClientAccountID, paymentMethod, fundRequest.Currency]);
  useEffect(() => {
    if (vp.toBool(ClientAccountsEnabled) && interacEmailsResponse.Success) {
      const InteracEmail = Object.values(interacEmailsResponse.InteracEmails).find((email) => email.IsDefault === '1');
      if (!InteracEmail) {
        setHasNoInterac(true);
        setCreateAccount(true);
      } else {
        setHasNoInterac(false);
        setCreateAccount(false);

        setCanSubmit(true);
      }
      setInteracInfo({
        isInteracQuestionsRequired: false,
        addInteracEmail: false,
        interacDetails: {
          Question: InteracEmail?.Question || '',
          Answer: InteracEmail?.Answer || '',
          RecipientPhoneNumber: InteracEmail?.RecipientPhoneNumber || '',
          RecipientName: InteracEmail?.RecipientName || '',
          RecipientEmailAddress: InteracEmail?.RecipientEmailAddress || '',
          ClientReferenceNumber: '',
        },
      });
    }
  }, [interacEmailsResponse]);
  useEffect(() => {
    // client accout bank
    if (vp.toBool(ClientAccountsEnabled) && clientBankAccountsResponse.Success) {
      if (
        fundRequest.PaymentMethod === 'Bank Account' &&
        (Object.keys(clientBankAccountsResponse.BankAccounts).length === 0 ||
          !Object.values(clientBankAccountsResponse.BankAccounts || {}).filter(
            (account) => account.IsDefault === '1' && account.Currency === fundRequest.Currency,
          )[0])
      ) {
        setHasNoBankAccount(true);
        setCreateAccount(true);
        setCommon((prevCommon) => ({
          ...prevCommon,
          CompanyName: clientAccountDetail?.ClientName || '',
        }));
      } else {
        setSelectedBank(
          Object.values(clientBankAccountsResponse.BankAccounts || {}).filter(
            (account) => account.ClientAccountID === clientAccountDetail?.ClientAccountID && account.IsDefault === '1',
          )[0],
        );
        setHasNoBankAccount(false);
        setCreateAccount(false);
        setCanSubmit(true);
      }
    }
  }, [clientBankAccountsResponse]);

  useEffect(() => {
    if (fundRequest.PaymentMethod === 'Credit Card' && selectedCreditCard) {
      setHasNoCreditCard(false);
      setCreateAccount(false);
      setCanSubmit(true);
    } else if (
      fundRequest.PaymentMethod === 'Credit Card' &&
      (selectedCreditCard === undefined || !selectedCreditCard)
    ) {
      setHasNoCreditCard(true);
      setCreateAccount(true);
      setCanSubmit(true);
    }
  }, [selectedCreditCard]);

  const getElinxSettings = async () => {
    try {
      const res = await fetchElinxSettings({});
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      } else {
        setIsVisible(false);
        setFundRequest({
          ...fundRequest,
          SendNotification: toBool(res.ELinxSettings.ReceiverNotificationsEnabled),
          SendReminder: toBool(res.ELinxSettings.ELinxReminderEnabled),
          ReminderFrequency: res.ELinxSettings.ELinxReminderFrequency,
          NotificationMethod: res.ELinxSettings.DefaultNotificationMethod,
        });
      }
    } catch (err) {
      console.error(err);
      dialog.caution('Error', 'Failed to Fetch eLinx Settings', {
        abortLabel: 'Dismiss',
      });
    }
  };

  useEffect(() => {
    fetchFinancialInstitutions({
      Country,
    });
    getElinxSettings();
  }, []);

  useEffect(() => {
    if (financialInstitutionsResp.Success) {
      const formattedFinancialInstitutions: FinancialInstitutionsType = Object.keys(
        financialInstitutionsResp.FinancialInstitutions,
      ).reduce((formatted: FinancialInstitutionsType, key) => {
        const { FinancialInstitutionNumber, FIName } = financialInstitutionsResp.FinancialInstitutions[key];
        formatted[FinancialInstitutionNumber] = `${FinancialInstitutionNumber} <br/> ${FIName}`;

        return formatted;
      }, {});
      setFormattedFinancialInstitutions(formattedFinancialInstitutions);
    }
  }, [financialInstitutionsResp]);

  useEffect(() => {
    if (financialInstitutionBranchesResponse.Success) {
      const formattedFinancialInstitutionBranches: FinancialInstitutionsType =
        financialInstitutionBranchesResponse.Branches.reduce((formatted: FinancialInstitutionsType, branch) => {
          const { BranchTransitNumber, Address, TownCity, PostalCode, Province } = branch;
          formatted[BranchTransitNumber] =
            `${BranchTransitNumber} <br/> ${Address}, ${TownCity}, ${PostalCode}, ${Province}`;

          return formatted;
        }, {});

      setFormattedFinancialInstitutionBranches(formattedFinancialInstitutionBranches);
    }
  }, [financialInstitutionBranchesResponse]);

  useEffect(() => {
    if (canSubmit === true) {
      if (isCreateAccount) {
        if (getAccountCreationValue(fundRequest.PaymentMethod) === 'bank') {
          const NewBankAccount: vp.ClientBankAccountsLists = {
            ABARoutingNumber: String(fundRequest.ABARoutingNumber),
            AccountHolderName:
              fundRequest.CompanyName || `${fundRequest.FirstName || ''} ${fundRequest.LastName || ''}`,
            AccountNumber: String(fundRequest.AccountNumber),
            Address: `${fundRequest.Address1 || ''} ${fundRequest.Address2 || ''}`,
            BranchTransitNumber: String(fundRequest.BranchTransitNumber),
            City: fundRequest.City || '',
            ClientReferenceNumber: fundRequest.ClientReferenceNumber || '',
            Currency: fundRequest.Currency || '',
            EmailAddress: '',
            FinancialInstitutionNumber: String(fundRequest.FinancialInstitutionNumber),
            'PostalCode/ZipCode': fundRequest.PostalCode || '',
            'Province/State': fundRequest.Province || '',
            Token: '',
            Phone: '',
            BankID: '',
            ClientAccountID: '',
            ClientControlledBankAccount: '',
            DateAdded: '',
            FlinksAccountID: '',
            FlinksLoginID: '',
            InstitutionName: '',
            InveriteRequestGUID: '',
            IsDefault: '',
            PlaidAccountID: '',
            FiservToken: '',
            IntegrationType: '',
            MxAuthorizationCode: '',
            MxProcessorToken: '',
            PlaidProcessorToken: '',
          };
          setSelectedBank(NewBankAccount);
        }
      }
    }
  }, [canSubmit, fundRequest]);

  const SingleCollectionContext: SingleCollectionCtx = {
    showSingleCollectionErrorAlert,
    singleCollectionError,
    showSingleCollectionConfirmAlert,
  };

  useEffect(() => {
    const bankAccountDetail = selectedBank;
    const clientAccountsEnabled = vp.toBool(ClientAccountsEnabled);
    const nameParts = bankAccountDetail?.AccountHolderName?.trim().split(' ') || '';
    const isCompanyName =
      (nameParts.length > 2 || nameParts.length < 2) && fundRequest?.PaymentMethod !== 'VoPay Instant';
    setCommon({
      PortalUser: AccountID || '',
      Amount: fundRequest.Amount || 0,
      FinancialInstitutionNumber: clientAccountsEnabled ? '' : bankAccountDetail?.FinancialInstitutionNumber || '',
      BranchTransitNumber: clientAccountsEnabled ? '' : bankAccountDetail?.BranchTransitNumber || '',
      ABARoutingNumber: clientAccountsEnabled ? '' : bankAccountDetail?.ABARoutingNumber || '',
      AccountNumber: clientAccountsEnabled ? '' : bankAccountDetail?.AccountNumber || '',
      FirstName: !isCompanyName ? bankAccountDetail?.AccountHolderName.split(' ')[0] || '' : '',
      LastName: !isCompanyName ? bankAccountDetail?.AccountHolderName.split(' ')[1] || '' : ' ',
      CompanyName: isCompanyName ? bankAccountDetail?.AccountHolderName || '' : '',
      PhoneNumber: vp.NumbersOnly(bankAccountDetail?.Phone || ''),
      Address1: bankAccountDetail?.Address || '',
      City: bankAccountDetail?.City || '',
      Province: bankAccountDetail?.['Province/State'] || '',
      Country: Country || '',
      Token: clientAccountsEnabled ? '' : bankAccountDetail?.Token || '',
      PostalCode: bankAccountDetail?.['PostalCode/ZipCode'] || '',
      ClientReferenceNumber: bankAccountDetail?.ClientReferenceNumber || '',
      FlinksAccountID: clientAccountsEnabled ? '' : bankAccountDetail?.FlinksAccountID || '',
      FlinksLoginID: clientAccountsEnabled ? '' : bankAccountDetail?.FlinksLoginID || '',
      TransactionLabel: fundRequest.TransactionLabel || '',
    });
  }, [selectedBank]);

  const setAccountDefault = (tokenAccount: string) => {
    // set my bank account
    const ClientAccountID = contactClientAccountID;
    setDefautlBankAccount({
      ClientAccountID,
      SetAsDefault: true,
      Token: tokenAccount,
      Purpose: 'operational',
      PortalUser: LoginUserName,
    });
  };

  useEffect(() => {
    if (shouldTokenize) {
      if (tokenizeResponse.Success) {
        setCommon((prevCommon) => ({
          ...prevCommon,
          Token: tokenizeResponse.Token,
        }));
        if (vp.toBool(ClientAccountsEnabled)) {
          setAccountDefault(tokenizeResponse.Token);
        } else {
          setCanSubmitTransaction(true);
          setCanSubmit(true);
        }
      } else {
        setShowSingleCollectionErrorAlert(true);
        setSingleCollectionError(tokenizeResponse.ErrorMessage);
      }
      setShouldTokenize(false);
    }
  }, [tokenizeResponse]);

  useEffect(() => {
    if (setDefautlBankAccountResponse.Success) {
      setCanSubmitTransaction(true);
      setCanSubmit(true);
    }
  }, [setDefautlBankAccountResponse]);

  useEffect(() => {
    if (shouldFundEft) {
      const { Success, ErrorMessage } = eftFundResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/EFTFunding/${eftFundResponse.TransactionID}/${
            fundRequest?.Currency || ''
          }/`,
          state: {
            transactionId: eftFundResponse.TransactionID,
            activeModule: 'singleCollection',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }
        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSingleCollectionErrorAlert(true);
        setSingleCollectionError(ErrorMessage);
      }
      setShouldFundEft(false);
    }
    if (shouldFundScheduledEft) {
      const { Success, ErrorMessage } = scheduledEftFundResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${scheduledEftFundResponse.ScheduledTransactionID}/`,
          state: {
            transactionId: scheduledEftFundResponse.ScheduledTransactionID,
            activeModule: 'singleCollection',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('transactions');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSingleCollectionErrorAlert(true);
        setSingleCollectionError(ErrorMessage);
      }
      setShouldFundScheduledEft(false);
    }

    if (shouldFundInteracETransfer) {
      const { Success, ErrorMessage } = interacMoneyRequestResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/InteracMoneyRequest/${interacMoneyRequestResponse.TransactionID}/${
            fundRequest.Currency || ''
          }/`,
          state: {
            transactionId: interacMoneyRequestResponse.TransactionID,
            activeModule: 'singleCollection',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSingleCollectionErrorAlert(true);
        setSingleCollectionError(ErrorMessage);
      }
      setShouldFundInteracETransfer(false);
    }

    if (shouldACHFund && ACHFundResponse) {
      const { Success, ErrorMessage } = ACHFundResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/ACHFunding/${ACHFundResponse.TransactionID}/${
            fundRequest?.Currency || 'USD'
          }/`,
          state: {
            transactionId: ACHFundResponse.TransactionID,
            activeModule: 'singleCollection',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSingleCollectionErrorAlert(true);
        setSingleCollectionError(ErrorMessage);
      }
      setShouldACHFund(false);
    }

    if (shouldACHFundSchedule === true && ACHFundScheduleResponse) {
      const { Success, ErrorMessage } = ACHFundScheduleResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${ACHFundScheduleResponse.ScheduledTransactionID}/`,
          state: {
            transactionId: ACHFundScheduleResponse.ScheduledTransactionID,
            activeModule: 'singleCollection',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('transactions');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSingleCollectionErrorAlert(true);
        setSingleCollectionError(ErrorMessage);
      }
      setShouldACHFundSchedule(false);
    }

    if (shouldChargeCreditCard === true) {
      const { Success, ErrorMessage } = chargeCreditCardResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/CreditCard/${chargeCreditCardResponse.TransactionID}/${
            fundRequest?.Currency || ''
          }/`,
          state: {
            transactionId: chargeCreditCardResponse.TransactionID,
            activeModule: 'singleCollection',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSingleCollectionErrorAlert(true);
        setSingleCollectionError(ErrorMessage);
      }
      setShouldFundScheduledEft(false);
    }

    if (shouldPostEmbedUrl) {
      const { Success, ErrorMessage } = embedUrlResponse;

      if (Success === false && ErrorMessage !== '') {
        setShowSingleCollectionErrorAlert(true);
        setSingleCollectionError(ErrorMessage);
        setShouldPostEmbedUrl(false);
      }
      shouldResetPendingData(false);
    }
  }, [
    eftFundResponse,
    scheduledEftFundResponse,
    interacMoneyRequestResponse,
    ACHFundResponse,
    ACHFundScheduleResponse,
    chargeCreditCardResponse,
    embedUrlResponse,
  ]);

  useEffect(() => {
    if (embedUrlResponse.Success && shouldPostEmbedUrl) {
      setShouldSendPaylink(true);
      const now = moment().format('YYYY-MM-DD');
      const isToday =
        moment(now).format('YYYY-MM-DD') === moment(Number(fundRequest?.StartDate)).format('YYYY-MM-DD') ||
        fundRequest?.StartDate === undefined ||
        fundRequest?.StartDate === '';
      postSendPaylink({
        SenderName: fundRequest?.SenderName || '',
        ReceiverName: vp.toBool(ClientAccountsEnabled)
          ? clientAccountDetail?.ClientName || ''
          : fundRequest?.CompanyName
            ? `${fundRequest?.CompanyName || ''}`
            : fundRequest?.RecipientName
              ? `${fundRequest?.RecipientName || ''}`
              : `${fundRequest?.FirstName || ''} ${fundRequest?.LastName || ''}`,
        ReceiverEmailAddress: vp.toBool(ClientAccountsEnabled)
          ? clientAccountDetail?.EmailAddress || ''
          : fundRequest?.RecipientEmailAddress || '',
        ReceiverPhoneNumber: fundRequest?.ReceiverPhoneNumber || '',
        IframeKey: embedUrlResponse.IframeKey || '',
        InvoiceNumber: fundRequest?.InvoiceNumber || '',
        ClientReferenceNumber: fundRequest?.ClientReferenceNumber || '',
        Note: fundRequest?.Notes || '',
        RecipientMessage: fundRequest?.MessageForRecipient || '',
        PaymentType: 'fund',
        Amount: fundRequest?.Amount,
        Currency: fundRequest.Currency,
        Frequency: !isToday ? fundRequest?.ScheduleType : '',
        NameOfFrequency: !isToday ? fundRequest?.Frequency : '',
        ScheduleStartDate: !isToday ? moment(Number(fundRequest?.StartDate)).format('YYYY-MM-DD') : '',
        ScheduleEndDate:
          !isToday && fundRequest?.Ending === 'End Date'
            ? moment(Number(fundRequest?.EndDate)).format('YYYY-MM-DD')
            : '',
        EndingAfterPayments: !isToday && fundRequest?.Ending === 'Payments' ? fundRequest?.EndingAfterPayments : '',
        SemiMonthlyFrequencyType: !isToday ? fundRequest?.SemiMonthlyDates : '',
        ClientAccountID: contactClientAccountID,
        SendEmail: fundRequest?.SendEmail || false,
        BankingPaymentRail:
          fundRequest.PaymentMethod === 'Bank Account' ? fundRequest?.BankingPaymentRail || 'eft' : undefined,
        SendNotification: fundRequest.SendNotification,
        SendReminder: fundRequest.SendReminder,
        ReminderFrequency: fundRequest.ReminderFrequency,
        NotificationMethod: fundRequest.NotificationMethod,
      });
      setShouldPostEmbedUrl(false);
    }
  }, [embedUrlResponse.Success, shouldPostEmbedUrl]);

  useEffect(() => {
    if (paylinkResponse && shouldSendPaylink === true) {
      const { Success, ErrorMessage } = paylinkResponse;

      if (Success === true) {
        setPayLink(paylinkResponse.Link);
        viewState('paylink');
      } else if (Success === false) {
        setShowSendPaylinkErrorAlert(true);
        setSendPaylinkError(ErrorMessage);
      }
      setShouldSendPaylink(false);
    }
  }, [paylinkResponse]);

  useEffect(() => {
    if (singleCollectionValuesPresent && submissionConfirmed) {
      setSingleCollectionValuesPresent(false);
      setSubmissionConfirmed(false);
      shouldResetPendingData(true);

      if (
        (singleCollectionValues?.Contact === 'request' ||
          singleCollectionValues?.ProvideOrRequestPaymentInformation === 'request') &&
        (getAccountCreationValue(singleCollectionValues?.PaymentMethod) === 'bank' ||
          getAccountCreationValue(singleCollectionValues?.PaymentMethod) === 'creditCard' ||
          getAccountCreationValue(singleCollectionValues?.PaymentMethod) === 'googlePay' ||
          getAccountCreationValue(singleCollectionValues?.PaymentMethod) === 'e-transfer')
      ) {
        setShouldPostEmbedUrl(true);
        postEmbedUrlResponse(buildEmbedUrlParams(singleCollectionValues));
      } else if (
        singleCollectionValues?.PaymentMethod === 'Credit Card' &&
        !selectedCreditCard &&
        ((!vp.toBool(ClientAccountsEnabled) && singleCollectionValues?.Contact === 'provide') ||
          (vp.toBool(ClientAccountsEnabled) &&
            singleCollectionValues?.ProvideOrRequestPaymentInformation === 'provide'))
      ) {
        setShowElinxConnect(true);
      } else if (
        getAccountCreationValue(singleCollectionValues?.PaymentMethod) === 'e-transfer' ||
        getAccountCreationValue(singleCollectionValues?.PaymentMethod) === 'googlePay'
      ) {
        setCanSubmitTransaction(true);
        setCanSubmit(true);
      } else if (!isCreateAccount) {
        setCanSubmitTransaction(true);
      } else {
        setSubmitData(singleCollectionValues);
        if (vp.toBool(ClientAccountsEnabled)) {
          setCanSubmit(false);
          setShouldTokenize(true);
          postTokenize({
            PortalUser: common.PortalUser,
            AccountHolderName:
              common.CompanyName && common.CompanyName !== ''
                ? common.CompanyName
                : `${common.FirstName || ''} ${common.LastName || ''}`,
            AccountNumber: common.AccountNumber || singleCollectionValues?.AccountNumber,
            FinancialInstitutionNumber:
              common.FinancialInstitutionNumber || singleCollectionValues?.FinancialInstitutionNumber,
            BranchTransitNumber: common.BranchTransitNumber || singleCollectionValues?.BranchTransitNumber,
            ABARoutingNumber: common.ABARoutingNumber || singleCollectionValues?.ABARoutingNumber,
            ClientReferenceNumber: common.ClientReferenceNumber,
            ClientControlled: false,
            ClientAccountID: contactClientAccountID || '',
            Currency: singleCollectionValues?.Currency || 'CAD',
          });
          setCreateAccount(false);
        } else {
          setCanSubmitTransaction(true);
        }
      }
    }
  }, [singleCollectionValuesPresent, submissionConfirmed]);

  const dialogueHandlers = {
    handlePaylinkDialogue: (handlerProps: DialogueHandlerProps2<SendPaylinkDialogueHandler>) => {
      const { type, state: states, action = 'default' } = handlerProps;

      const dialogueHandlers2: SendPaylinkDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowSendPaylinkErrorAlert(false);
            },
          },
          view: {
            default: () => {
              setShowSendPaylinkErrorAlert(false);
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              setShowSendPaylinkSuccessAlert(false);
            },
          },
          view: {
            default: () => {
              setShowSendPaylinkSuccessAlert(false);
              onSubNavClick('paylinkHistory', '/elinx-history', 'eLinx History');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers2[type][states];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    if (canSubmitTransaction && canSubmit && Object.values(formErrors).length === 0) {
      if (
        fundRequest?.PaymentMethod === 'Bank Account' &&
        ((fundRequest?.ScheduleType === 'single' &&
          fundRequest?.TakeFromAccountOn &&
          vp.IsTodayDate(moment(Number(fundRequest.TakeFromAccountOn)).format('YYYY-MM-DD'))) ||
          (Country === 'CA' && fundRequest.Currency === 'USD'))
      ) {
        if (Country === 'US') {
          setShouldACHFund(true);
          postACHFund({
            ...common,
            Amount: fundRequest?.Amount || 0,
            Currency: fundRequest?.Currency || 'USD',
            Notes: fundRequest?.Notes,
            Address1: fundRequest?.Address1 || common?.Address1 || '',
            City: fundRequest?.City || common?.City || '',
            State: fundRequest?.Province || common?.Province || '',
            ZipCode: fundRequest?.PostalCode || common?.PostalCode || '',
            PhoneNumber: fundRequest?.RecipientPhoneNumber || common?.PhoneNumber || '',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? fundRequest.ClientAccount : '',
            IdentificationNumber: fundRequest?.IdentificationNumber || '',
            EmailAddress: fundRequest?.RecipientEmailAddress || '',
            SecCode: fundRequest?.SecCode || 'PPD',
          });
        } else {
          setShouldFundEft(true);
          postEftFund({
            ...common,
            Amount: fundRequest?.Amount || 0,
            Currency: fundRequest.Currency || '',
            Notes: fundRequest.Notes || '',
            ClientReferenceNumber: common?.ClientReferenceNumber || fundRequest?.ClientReferenceNumber || '',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? fundRequest.ClientAccount : '',
          });
        }
      } else if (
        (fundRequest?.PaymentMethod === 'Bank Account' && fundRequest?.ScheduleType === 'recurring') ||
        (fundRequest?.TakeFromAccountOn &&
          !vp.IsTodayDate(moment(Number(fundRequest.TakeFromAccountOn)).format('YYYY-MM-DD')))
      ) {
        if (Country === 'US') {
          setShouldACHFundSchedule(true);
          postACHFundSchedule({
            ...common,
            Amount: fundRequest?.Amount || 0,
            Frequency: fundRequest?.ScheduleType || '',
            NameOfFrequency: fundRequest?.Frequency || '',
            SemiMonthlyFrequencyType: fundRequest?.SemiMonthlyDates || '',
            PhoneNumber: fundRequest?.RecipientPhoneNumber || common?.PhoneNumber || '',
            ScheduleStartDate:
              fundRequest?.ScheduleType === 'recurring'
                ? moment(Number(fundRequest.StartDate)).format('YYYY-MM-DD') || ''
                : moment(Number(fundRequest.TakeFromAccountOn)).format('YYYY-MM-DD') || '',
            ScheduleEndDate:
              fundRequest.Ending === 'End Date' && fundRequest.ScheduleType === 'recurring'
                ? moment(Number(fundRequest.EndDate)).format('YYYY-MM-DD') || ''
                : '',
            EndingAfterPayments: fundRequest.Ending === 'Payments' ? fundRequest.EndingAfterPayments || '' : '',
            Description: fundRequest?.Description || '',
            Address1: fundRequest?.Address1 || common?.Address1 || '',
            City: fundRequest?.City || common?.City || '',
            State: fundRequest?.Province || common.Province || '',
            ZipCode: fundRequest?.PostalCode || common?.PostalCode,
            Currency: fundRequest?.Currency || 'USD',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? fundRequest.ClientAccount : '',
            IdentificationNumber: fundRequest.IdentificationNumber || '',
          });
        } else {
          setShouldFundScheduledEft(true);
          postScheduledEftFund({
            ...common,
            Amount: fundRequest?.Amount || 0,
            Frequency: fundRequest?.ScheduleType || '',
            NameOfFrequency: fundRequest?.Frequency || '',
            ScheduleStartDate:
              fundRequest?.ScheduleType === 'recurring'
                ? moment(Number(fundRequest.StartDate)).format('YYYY-MM-DD') || ''
                : moment(Number(fundRequest.TakeFromAccountOn)).format('YYYY-MM-DD') || '',
            ScheduleEndDate:
              fundRequest.Ending === 'End Date' && fundRequest.ScheduleType === 'recurring'
                ? moment(Number(fundRequest?.EndDate || '')).format('YYYY-MM-DD')
                : '',
            EndingAfterPayments: fundRequest.Ending === 'Payments' ? fundRequest.EndingAfterPayments || '' : '',
            Description: fundRequest?.Description || '',
            SemiMonthlyFrequencyType: fundRequest?.SemiMonthlyDates || '',
            EmailAddress: '',
            DOB: '',
            IPAddress: '',
            PlaidAccountID: selectedBank?.PlaidAccountID || '',
            PlaidPublicToken: '',
            PlaidAccessToken: '',
            PlaidProcessorToken: '',
            InveriteRequestGUID: selectedBank?.InveriteRequestGUID || '',
            IdempotencyKey: '',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? fundRequest.ClientAccount : '',
          });
        }
      } else if (fundRequest?.PaymentMethod === 'Interac e-Transfer Money Request') {
        setShouldFundInteracETransfer(true);
        postInteracMoneyRequest({
          PortalUser: AccountID || '',
          Amount: fundRequest?.Amount || 0,
          Currency: fundRequest?.Currency || '',
          EmailAddress: !vp.toBool(ClientAccountsEnabled) ? fundRequest?.RecipientEmailAddress || '' : '',
          RecipientName: fundRequest?.RecipientName || '',
          ClientReferenceNumber: fundRequest?.ClientReferenceNumber || '',
          Notes: fundRequest?.Notes || '',
          ClientAccountID: vp.toBool(ClientAccountsEnabled) ? fundRequest.ClientAccount : '',
          MessageForRecipient: fundRequest.Message || '',
          GenerateURL: false,
        });
      } else if (fundRequest.PaymentMethod === 'Credit Card') {
        if (addedCreditCard !== undefined || selectedCreditCard !== undefined || vp.toBool(ClientAccountsEnabled)) {
          setShouldChargeCreditCard(true);
          postChargeCreditCard({
            PortalUser: AccountID,
            Amount: fundRequest?.Amount || 0,
            Currency: fundRequest?.Currency || '',
            Country,
            CreditCardToken: addedCreditCard
              ? addedCreditCard?.CreditCardToken
              : selectedCreditCard?.CreditCardToken || '',
            IdempotencyKey: '',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? fundRequest.ClientAccount : '',
          });
        }
      }
    }
    setCanSubmitTransaction(false);
  }, [canSubmitTransaction, canSubmit]);

  const onCompleteToken = (token: string, paymentMethodToken: string) => {
    if (paymentMethodToken === 'creditcard') {
      setIsAddingNewCreditCard(true);
      fetchCreditCards({
        PortalUser: LoginUserName,
        ClientControlled: vp.toBool(ClientAccountsEnabled),
        ClientAccountID: vp.toBool(ClientAccountsEnabled) ? paymentMethodToken : undefined,
        CreditCardToken: token,
      });
    }
  };

  const handlers = {
    handleSingleCollectionDialogue: (handlerProps: DialogueHandlerProps<SingleCollectionDialogueHandler>) => {
      const { type, state: states, action = 'default' } = handlerProps;
      const dialogueHandler: SingleCollectionDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowSingleCollectionErrorAlert(false);
            },
          },
          cancel: {
            default: () => {
              setShowSingleCollectionErrorAlert(false);
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              setSubmissionConfirmed(true);
              setShowSingleCollectionConfirmAlert(false);
            },
          },
          cancel: {
            default: () => {
              setShowSingleCollectionConfirmAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandler[type][states];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },

    handleSubmit: (values: SingleCollection) => {
      if (isUnderReview) {
        setSubmissionConfirmed(true);
        setSingleCollectionValues(values);
        setSingleCollectionValuesPresent(true);
      } else {
        setFundRequest({
          ...values,
          Amount: Number(String(values.Amount)?.replaceAll(',', '')),
        });
        setIsUnderReview(true);
      }
    },
    onSingleCollectionChange: (
      _values: SingleCollection,
      errors: FormikErrors<SingleCollection>,
      touched: FormikTouched<SingleCollection>,
    ) => {
      resetBankAccount();
      setFormErrors(errors);
      setFormTouched(touched);
      setFundRequest(_values);
      setContactClientAccountID(_values.ClientAccount);
      setPaymentMethod(_values.PaymentMethod);
      setSelectedCurrency(_values?.Currency || 'CAD');

      if (
        _values.FinancialInstitutionNumber !== '' &&
        fundRequest.FinancialInstitutionNumber !== _values.FinancialInstitutionNumber
      ) {
        const FinancialInstitutionID = getFinancialInstitutionIDByNumber(_values.FinancialInstitutionNumber);
        setFormattedFinancialInstitutionBranches({});
        if (FinancialInstitutionID) {
          fetchFinancialInstitutionBranches({ FinancialInstitutionID: Number(FinancialInstitutionID) });
        }
      }

      if (fundRequest.Contact !== _values.Contact || fundRequest.PaymentMethod !== _values.PaymentMethod) {
        if (getAccountCreationValue(_values.PaymentMethod) === 'bank' && bankAccountInfo !== undefined) {
          setSelectedBank(bankAccountInfo);
        }

        if (getAccountCreationValue(_values.PaymentMethod) === 'creditCard') {
          if (_values.Contact) setSelectedCreditCard(contactCreditCardMap[_values.Contact]);
        }
      }

      if (_values.FinancialInstitutionNumber === '') {
        setFormattedFinancialInstitutionBranches({});
      }

      setCreateAccount(
        _values.Contact === 'provide' ||
          (getAccountCreationValue(fundRequest.PaymentMethod) === 'bank' && hasNoBankAccount) ||
          (getAccountCreationValue(fundRequest.PaymentMethod) === 'creditCard' && hasNoCreditCard),
      );
      setCanSubmit(true);
    },

    shouldShowElinxConnect: (showElinx: boolean) => {
      setShowElinxConnect(showElinx);
    },

    handleElixConnect: (event: EventMessage) => {
      onCompleteToken(event.data.Token, event.data.PaymentMethod);
    },
  };

  useEffect(() => {
    if (vp.toBool(ClientAccountsEnabled) && success) {
      const creditCard = creditCards.find((card) => vp.toBool(card.IsDefault));
      if (creditCard) {
        setHasNoCreditCard(false);
      } else {
        setHasNoCreditCard(true);
      }
    } else if (success && creditCards.length > 0 && isAddingNewCreditCard) {
      const creditCard = creditCards.pop();
      setAddedCreditCard(creditCard);
      setCanSubmitTransaction(true);
      setIsAddingNewCreditCard(false);
    }
  }, [creditCards]);

  useEffect(() => {
    if (submitTransaction) {
      setTimeout(() => {
        setSubmitTransaction(false);
      }, 500);
    }
  }, [submitTransaction]);

  useEffect(() => {
    if (isUnderReview) {
      if (reviewRef !== null && reviewRef.current !== null) {
        reviewRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [isUnderReview]);

  useEffect(() => {
    if (tokenProp !== '') {
      fetchClientBankAccounts({
        PortalUser: LoginUserName,
        ClientControlled: false,
        Token: tokenProp,
        Timezone: userSettings.timezone,
      });
    }
  }, [tokenProp]);

  return (
    <div ref={reviewRef}>
      <Box mb="md" jc="sb" flx="col" gap="lg">
        <Box mb="md" jc="sb">
          {isUnderReview ? <Title title="Review and Submit" /> : <Title title={getModuleTitle()} />}
        </Box>
        <SendPaylinkContext.Provider value={SendPaylink}>
          <PendingDataProvider>
            <InteracInfoContext.Provider value={interacInfoWithUpdate}>
              <SingleCollectionBankInfoContext.Provider
                value={{
                  bankAccount: bankAccountInfo,
                  updateBankAccount,
                }}
              >
                {view === 'form' && (
                  <SingleCollectionForm
                    hasNoCreditCard={hasNoCreditCard}
                    hasNoBankAccount={hasNoBankAccount}
                    resetPendingData={resetPendingData}
                    hasNoInterac={hasNoInterac}
                    isUnderReview={isUnderReview}
                    setIsUnderReview={setIsUnderReview}
                    financialInstitutions={FinancialInstitutions}
                    financialInstitutionBranches={FormattedFinancialInstitutionBranches}
                    handlers={handlers}
                    defaultValues={fundRequest}
                    defaultTouches={formTouched}
                    SingleCollectionContext={SingleCollectionContext}
                    showElinxConnect={showElinxConnect}
                    setIsVisible={setIsVisible}
                    selectedCurrency={selectedCurrency}
                    selectedClientAccountDetail={setSelectedClientAccountDetail}
                    contactCreditCards={contactCreditCardMap}
                    accountHolderName={accountHolderName}
                  />
                )}
              </SingleCollectionBankInfoContext.Provider>
              {view === 'paylink' && paylinkResponse && <PaylinkCreationPage eLinx={paylinkResponse} />}
              <PaylinkDialogue handleDialogue={dialogueHandlers.handlePaylinkDialogue} />
            </InteracInfoContext.Provider>
          </PendingDataProvider>
        </SendPaylinkContext.Provider>
        <ElinxSettingsDialogue
          isVisible={isVisible}
          onCloseModal={() => setIsVisible(false)}
          onFetchElinxSettings={getElinxSettings}
        />
      </Box>
    </div>
  );

  function getFinancialInstitutionIDByNumber(number: string) {
    for (const key in financialInstitutionsResp.FinancialInstitutions) {
      if (financialInstitutionsResp.FinancialInstitutions[key].FinancialInstitutionNumber.toString() === number) {
        return financialInstitutionsResp.FinancialInstitutions[key].FinancialInstitutionID;
      }
    }
    return null; // Return null if the number is not found
  }

  function getAccountCreationValue(initialValue: string): string {
    if (initialValue === 'Bank Account') {
      return 'bank';
    }
    if (initialValue === 'VoPay Instant') {
      return 'bank';
    }
    if (initialValue === 'Interac e-Transfer Money Request') {
      return 'e-transfer';
    }
    if (initialValue === 'Credit Card') {
      return 'creditCard';
    }
    if (initialValue === 'Google Pay') {
      return 'googlePay';
    }
    return initialValue;
  }

  function setSubmitData(values: SingleCollection) {
    const clientAccountsEnabled = vp.toBool(ClientAccountsEnabled);
    const isCompanyName = values?.CompanyName !== '';

    setCommon({
      PortalUser: AccountID || '',
      Amount: values.Amount || 0,
      FinancialInstitutionNumber: clientAccountsEnabled ? '' : values?.FinancialInstitutionNumber || '',
      BranchTransitNumber: clientAccountsEnabled ? '' : values?.BranchTransitNumber || '',
      ABARoutingNumber: clientAccountsEnabled ? '' : values?.ABARoutingNumber || '',
      AccountNumber: clientAccountsEnabled ? '' : values?.AccountNumber || '',
      FirstName: !isCompanyName ? values?.FirstName || '' : '',
      LastName: !isCompanyName ? values?.LastName || '' : ' ',
      CompanyName: isCompanyName ? values?.CompanyName || '' : '',
      PhoneNumber: vp.NumbersOnly(values?.RecipientPhoneNumber || ''),
      Address1: values?.Address1 || '',
      City: values?.City || '',
      Province: values?.Province || '',
      Country: Country || '',
      PostalCode: values?.PostalCode || '',
      ClientReferenceNumber: values?.ClientReferenceNumber || '',
      FlinksAccountID: '',
      FlinksLoginID: '',
      TransactionLabel: values.TransactionLabel || '',
      Token: '',
    });
  }

  function buildEmbedUrlParams(values: NewAccountData): vp.EmbedUrlRequest {
    const embedUrlData: vp.EmbedUrlRequest = {
      RedirectURL: 'https://request-dev.vopay.com/',
      Language: values.language || 'en',
      Version: 'v2',
      RedirectMethod: 'JavascriptMessage',
      PaymentSelectionMethod:
        getAccountCreationValue(values.PaymentMethod) === 'creditCard'
          ? 'credit'
          : getAccountCreationValue(values.PaymentMethod) === 'e-transfer'
            ? 'email'
            : getAccountCreationValue(values.PaymentMethod) === 'googlePay'
              ? 'googlepay'
              : 'bank',
      AccountSelectionMethod: values.AccountConnectionMethod.includes('|')
        ? 'any'
        : values.AccountConnectionMethod.toLowerCase(),
      ClientControlled: vp.toBool(ClientAccountsEnabled),
      ClientReferenceNumber: values.ClientReferenceNumber,
      TermsAndConditions: values.termsAndConditions,
      Country: values.Country,
      ClientAccountID: contactClientAccountID,
    };
    return embedUrlData;
  }
};

export default SingleCollectionContainer;
